import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryClient, useQuery } from 'react-query';
import currencyFormatter from 'currency-formatter';

import {
  forceUpdateSubscription,
  getSubscription,
  getSubscriptionOriginOrder,
  orderNow,
  requestPaymentChange,
  UnauthorizedError,
} from '../api';
import { useGlobalContext } from './GlobalContext';
import { useSubDetailToast } from './useSubDetailToast';
import { stubSubscriptionItemDetails } from '../subsStubData';
import {
  calculateLinesSubtotal,
  getNextBillingDate,
  getPrepaidCurrentOrdersCount,
  getSubscriptionLevelDiscount,
} from '../utils';
import { useNotifs } from '../../utils/useNotif';
import { wrapDynamicBabItems } from '../../utils/BundleUtils';
import { LinkButton } from '../SubsDetail.components';
import dayjs from 'dayjs';
import { getBatchingNextBillingDate } from '../../../utils/batching';
import { isObject } from 'lodash';
import { useShopConfiguration } from './useSubDetail';

type AuthData = {
  customerId: string;
  authToken: string;
};

interface UseSubscriptionProps {
  subscriptionId: string;
  queryClient: QueryClient;
  closeOrderNowConfirmationModal: () => void;
  isLoadingOrderNow: boolean;
  nextBAR: React.MutableRefObject<SubscriptionBillingAttemptRetry | undefined>;
  sendEmailIfAddPaymentFails: boolean;
  setForceUpdateError;
  setIsLoadingOrderNow: React.Dispatch<React.SetStateAction<boolean>>;
  setOriginOrder: React.Dispatch<React.SetStateAction<OriginOrder | null>>;
  setPaymentResponse: React.Dispatch<React.SetStateAction<string | null>>;
  subscriptionIsInDunning: boolean;
  subscriptionQuantityChanges: React.MutableRefObject<Subscription | undefined>;
}

interface UseSubscription {
  isActive: boolean;
  isCancelled: boolean;
  isExpired: boolean;
  isFailed: boolean;
  isPaused: boolean;
  isRetryingManually: boolean;
  isUpdatingPaymentMethod: boolean;
  orderSubmitted: boolean;
  onOrderNowConfirm: (manualRetry?: boolean) => void;
  shouldDisableOrderNowButton: boolean;
  setShouldDisableOrderNowButton: React.Dispatch<React.SetStateAction<boolean>>;
  subscription: any;
}

export const useSubscription = ({
  isLoadingOrderNow,
  nextBAR,
  closeOrderNowConfirmationModal,
  sendEmailIfAddPaymentFails,
  setIsLoadingOrderNow,
  setOriginOrder,
  setPaymentResponse,
  setForceUpdateError,
  subscriptionIsInDunning,
  subscriptionQuantityChanges,
  queryClient,
  customerId,
  subscriptionId,
  authToken,
}: AuthData & UseSubscriptionProps): UseSubscription => {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast, showWarningToast } =
    useSubDetailToast();
  const { logout, customerEmail } = useGlobalContext();
  const { addNotif, resetNotifs } = useNotifs();
  const [shouldDisableOrderNowButton, setShouldDisableOrderNowButton] =
    useState(true);
  const [orderSubmitted, setOrderSubmitted] = useState(false);

  const { data: subscription } = useQuery(
    ['subscription', customerId, subscriptionId],
    async () => {
      if (subscriptionId?.includes('TEST')) {
        return stubSubscriptionItemDetails(subscriptionId);
      }
      let response = await getSubscription(
        customerId,
        subscriptionId,
        authToken,
      );
      response = wrapDynamicBabItems(response);

      if (response?.IsBilling || response?.IsUpdating) {
        try {
          const res = await forceUpdateSubscription(
            customerId,
            subscriptionId,
            authToken,
          );

          if (res.status === 204) {
            response = await getSubscription(
              customerId,
              subscriptionId,
              authToken,
            );
            response.IsBilling = false;
            response.IsUpdating = false;
          } else {
            setForceUpdateError(true);
          }
        } catch (e) {
          console.error(e);

          if (e instanceof UnauthorizedError) {
            logout();
          }
        }
      }

      try {
        const originOrderResponse = await getSubscriptionOriginOrder(
          customerId,
          subscriptionId,
          authToken,
        );

        setOriginOrder(originOrderResponse);
        // make this data easily available on the window for custom theme dev purposes
        window.Awtomic.subscriptionOriginOrder = originOrderResponse;
      } catch (error) {
        // nothing to do here as it's a subscription without origin order
      }

      const { SubscriptionLevelDiscount } = getSubscriptionLevelDiscount(
        response?.Lines,
      );

      const SubscriptionSubTotal = calculateLinesSubtotal(
        response?.Lines || [],
      ).toFixed(2);

      const isPrepaidV2 = response?.Lines?.some(({ PrepaidV2 }) => PrepaidV2);
      let PrepaidV2CurrentOrdersCount;
      let PrepaidV2TotalOrdersCount;
      let PrepaidV2DiscountFormatedAmount;
      if (isPrepaidV2) {
        response.prepaidV2Discounts = getPrepaidCurrentOrdersCount(
          response?.Lines,
        );
      }

      const isActive = response?.SubscriptionStatus === 'ACTIVE';
      const isFailed = response?.SubscriptionStatus === 'FAILED';
      const isRetryingManually = response?.RetryingManually;
      const isBillingOrUpdating = response?.IsBilling || response?.IsUpdating;
      const isBillingScheduled = response?.IsBillingScheduled;
      const isInBillingProcess = isBillingOrUpdating || isBillingScheduled;
      const isUpdatingPaymentMethod = response?.UpdatingPaymentMethod;
      const nextBARDate = nextBAR.current?.NextBillingDate;
      const isDelayedDueToInventory =
        response?.OriginalNextBillingDate && isActive;

      resetNotifs();

      if (isFailed && !isRetryingManually) {
        addNotif(
          <>
            {t('subscription.failed_status_payment_message_1')}
            <LinkButton
              className="subscription-payment-link-button"
              onClick={handlePaymentUpdate}
            >
              {t('subscription.failed_status_payment_cta')}
            </LinkButton>
            {t('subscription.failed_status_payment_message_2')}
          </>,
          {
            type: 'error',
          },
        );
      }

      if (isDelayedDueToInventory) {
        showWarningToast(t('subscription.delayedNotification'));
      }

      if (isRetryingManually && isActive && nextBARDate) {
        showSuccessToast(
          <>
            {t('subscription.retrying_status_message')}
            <LinkButton className="subscription-payment-link-button" href="">
              {t('subscription.retrying_status_cta')}
            </LinkButton>
          </>,
          60000, // 1 minute
        );
      }

      if (isBillingScheduled) {
        addNotif(t('subscription.subscription_processing'), { type: 'info' });
      }

      if (isBillingOrUpdating) {
        addNotif(t('subscription.subscription_processing'), { type: 'info' });
      }

      if (
        isUpdatingPaymentMethod &&
        !isRetryingManually &&
        !isInBillingProcess &&
        !isLoadingOrderNow &&
        isActive
      ) {
        showSuccessToast(
          <>
            {t('subscription.new_payment_method_dunning.message', {
              date: nextBARDate
                ? new Date(nextBARDate).toLocaleDateString()
                : '',
            })}{' '}
            <LinkButton
              className="subscription-payment-link-button"
              href=""
              onClick={() => onOrderNowConfirm(true)}
            >
              {t('subscription.new_payment_method_dunning.cta')}
            </LinkButton>
          </>,
          60000, // 1 minute
        );
      }

      const FormattedSubscriptionSubtotal = currencyFormatter.format(
        SubscriptionSubTotal,
        { code: response.CurrencyCode },
      );

      // make this data easily available on the window for custom theme dev purposes
      window.Awtomic = window.Awtomic || {};
      window.Awtomic.subscriptionDetails = response;

      // send custom event so that mechants can listen for box changes on the FE
      // to show custom UI afterwards
      const cpLoadedEventName = 'awtomic-cp-loaded';
      const event = new CustomEvent(cpLoadedEventName, {
        detail: window.Awtomic.subscriptionDetails,
      });
      window.dispatchEvent(event);

      const newSubscription: Subscription = {
        ...response,
        SubscriptionSubTotal,
        FormattedSubscriptionSubtotal,
        SubscriptionLevelDiscount,
        PrepaidV2CurrentOrdersCount,
        PrepaidV2TotalOrdersCount,
        PrepaidV2DiscountFormatedAmount,
      };
      subscriptionQuantityChanges.current = newSubscription;
      return newSubscription;
    },
  );

  const shopConfiguration = useShopConfiguration(subscription);

  const handlePaymentUpdate = async () => {
    try {
      const res = await requestPaymentChange(
        customerId,
        subscriptionId,
        authToken,
      );
      const _customerEmail = customerEmail || subscription?.Customer?.Email;

      setPaymentResponse(res);

      if (sendEmailIfAddPaymentFails) {
        showErrorToast(
          t('subscription.update_payment_success_payment_error', {
            email: _customerEmail,
          }),
        );
      } else {
        showSuccessToast(
          t(
            subscriptionIsInDunning
              ? 'subscription.update_payment_success_dunning'
              : 'subscription.update_payment_success',
            { email: _customerEmail },
          ),
          60000, // 60 seconds
        );
      }
    } catch (err) {
      console.error(err);
      addNotif(t('subscription.update_payment_error'), { type: 'error' });
    }
  };

  const onOrderNowConfirm = async (manualRetry = false) => {
    try {
      setIsLoadingOrderNow(true);
      await orderNow(customerId, subscriptionId, manualRetry, authToken);

      setIsLoadingOrderNow(false);
      setShouldDisableOrderNowButton(true);
      setOrderSubmitted(true);
      closeOrderNowConfirmationModal();

      if (!isFailed && !manualRetry) {
        const isBatchedV2 =
          (isObject(subscription?.BatchingConfiguration) as any) &&
          subscription?.BatchingConfiguration?.anchor !== '';

        const [interval, intervalNumber] =
          subscription?.BillingIntervalBillingCount.split('#') || [];

        const nextDate = isBatchedV2
          ? dayjs(
              // In order to force the calculation from the current next billing date
              // we set it as retry with that next billing date as the original
              // next billing date, so the function starts the calculations from that day
              getBatchingNextBillingDate({
                batchingConfiguration: subscription.BatchingConfiguration,
                billingCount: intervalNumber,
                billingInterval: interval,
                shopDomainOffset: shopConfiguration?.ShopDomainOffset,
                shopDomainTZ: shopConfiguration?.ShopDomainTZ,
                isRetry: true,
                originalNextBillingDate: subscription.NextBillingDate,
              }),
            )
          : getNextBillingDate(subscription);

        queryClient.setQueryData(['subscription', customerId, subscriptionId], {
          ...subscription,
          NextBillingDate: nextDate,
        });
        showSuccessToast(t('subscription.order_now_success'));
      }
    } catch (e) {
      showErrorToast(t('subscription.order_now_error'));
      setIsLoadingOrderNow(false);
      setOrderSubmitted(false);
      closeOrderNowConfirmationModal();
    }
  };

  const isActive = subscription?.SubscriptionStatus === 'ACTIVE';
  const isCancelled = subscription?.SubscriptionStatus === 'CANCELLED';
  const isExpired = subscription?.SubscriptionStatus === 'EXPIRED';
  const isFailed = subscription?.SubscriptionStatus === 'FAILED';
  const isPaused = subscription?.SubscriptionStatus === 'PAUSED';
  const isRetryingManually = subscription?.RetryingManually;
  const isUpdatingPaymentMethod = subscription?.UpdatingPaymentMethod;

  return {
    isActive,
    isCancelled,
    isExpired,
    isFailed,
    isPaused,
    isRetryingManually,
    isUpdatingPaymentMethod,
    shouldDisableOrderNowButton,
    setShouldDisableOrderNowButton,
    orderSubmitted,
    onOrderNowConfirm,
    subscription,
  };
};
